import { useCallback, useMemo, useState } from 'react';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog';
import { WorkflowParameter } from 'api/model';
import WorkflowParameterValuesForm from './WorkflowParameterValuesForm';
import { areParameterValuesValid, computeParameterDefaults } from './workflowParameterValues';

export type Props = {
  open: boolean;
  title?: string;
  confirmText?: string;
  parameters: WorkflowParameter[];
  latestExecutionParameters?: Record<string, string>;
  onSubmit: (values: Record<string, string>) => void;
  onCancel: () => void;
};

type ContentProps = Omit<Props, 'open'>;

const DialogContent = ({
  title,
  confirmText,
  parameters,
  latestExecutionParameters,
  onSubmit,
  onCancel,
}: ContentProps) => {
  const [values, setValues] = useState<Record<string, string>>(() =>
    computeParameterDefaults(parameters, latestExecutionParameters),
  );
  const valid = useMemo(() => areParameterValuesValid(parameters, values), [parameters, values]);

  const handleConfirm = useCallback(() => {
    if (!valid) return;
    onSubmit(values);
  }, [onSubmit, values, valid]);

  return (
    <ConfirmationDialog
      title={title ?? 'Workflow Parameters'}
      open
      onConfirm={handleConfirm}
      onCancel={onCancel}
      buttonsSettings={{ confirmText: confirmText ?? 'Submit', cancelText: 'Cancel' }}
      confirmDisabled={!valid}
    >
      <WorkflowParameterValuesForm parameters={parameters} values={values} onChange={setValues} />
    </ConfirmationDialog>
  );
};

const WorkflowParameterValuesDialog = ({ open, ...rest }: Props) => {
  if (!open) return null;
  return <DialogContent {...rest} />;
};

export default WorkflowParameterValuesDialog;
